// Stewardship OS design tokens.
//
// The Stewardship design system spec, expressed as TypeScript constants.
// These values mirror the CSS custom properties declared in
// web/src/styles.css (:root block) — changing a token here without
// updating the CSS variable (or vice versa) is a bug.
//
// Why both CSS vars AND TS tokens:
//   * CSS vars are the idiom for global styles + ad-hoc CSS.
//   * TS tokens are typeable + autocompleted in component code, which
//     is where 80% of new styling work happens going forward.
//   * Components can opt for inline styles referencing `tokens.*` when
//     a new one-off needs a color without creating a CSS class.
//
// April 2026: extended from the v1 minimal set to the full handoff
// design system. Three colors with one job each: navy = foundation,
// blue = action, yellow = exclamation point. Shadows are always
// navy-tinted (the brand's signature). Headings are always uppercase
// with letter-spacing.

// ─── Colors ────────────────────────────────────────────────────────

export const colors = {
  // Brand core
  navy: "#022c51",
  navyDeep: "#011e38",
  navyMid: "#033d70",
  // Navy as a SURFACE (navy chrome: nav bar, active tabs, primary buttons,
  // toasts). Distinct from `navy` because `navy` is the primary INK token,
  // and dark mode flips --navy to a light ink to recolor all body text at
  // once. A background painted with --navy would therefore invert to a
  // light surface in dark mode (white text → invisible). --navy-surface is
  // deliberately NOT remapped in html.dark, so navy chrome stays navy in
  // both themes. Use this for `background`/solid navy fills; use `navy` for
  // text ink. Same #022c51 value — the split is purely so dark mode can
  // treat ink and surface differently.
  navySurface: "#022c51",
  blue: "#4e98c6",
  blueLight: "#5ba3d0",
  blueDark: "#3a7ea8",
  yellow: "#fed766",
  yellowDeep: "#e8c24a",

  // Neutrals
  white: "#ffffff",
  lightGray: "#f4f6f8",
  midGray: "#e0e6ec",
  // Secondary text — reconciled to gray-700 per the design plan (2026-07-24).
  darkGray: "#4a5b6e",
  darkGrayLight: "#767676",
  blueTint: "#deeef7",
  blueTintDeep: "#c8dff0",

  // Semantic feedback (status pills, banners, severity) — reconciled to the
  // design plan (2026-07-24): brighter success, amber (not mustard) warning.
  success: "#1f8a4c",
  successBg: "#e8f5ee",
  warning: "#b45309",
  warningBg: "#fdf2e3",
  danger: "#c0392b",
  dangerHover: "#a5322a", // destructive-button hover (darker brand red)
  dangerBg: "#fdecea",
  dangerBorder: "#fecaca",
  pinnedBg: "#fff7ce",

  // Attention / flag semantics — the amber a record detail page uses for
  // "needs a human decision" (unassigned advisor, migration duplicate).
  // Mirrors --attention / --attention-tint / --attention-border in
  // styles.css (dark mode remaps those vars). Same #b45309 as the
  // care-status "due" / status-dot "review" amber.
  attention: "#b45309",
  attentionTint: "#fdf2e3",
  attentionBorder: "#f0d3a8",

  // Foreground aliases
  fg1: "#022c51",
  fg2: "#4a5b6e", // secondary text (== darkGray), gray-700 per the design plan
  fg3: "#555555",
  fgMuted: "#777777",
  fgOnNavy: "#ffffff",
  fgOnNavy2: "rgba(255, 255, 255, 0.75)",
  fgOnNavy3: "rgba(255, 255, 255, 0.55)",

  // Navbar-specific overlay values (sticky navy + on-navy controls)
  navbarBorder: "rgba(255, 255, 255, 0.06)",
  navbarLinkDim: "rgba(255, 255, 255, 0.78)",
  navbarLinkHoverBg: "rgba(255, 255, 255, 0.08)",
  navbarSearchBg: "rgba(255, 255, 255, 0.08)",
} as const;

// ─── Mode tokens ───────────────────────────────────────────────────
//
// Service Records design system uses three workflow modes, each with
// a signature hue:
//
//   * Opportunity (`opp`)    — blue  — pre-sale, conversion-focused
//   * Processing  (`proc`)   — amber — in-flight, milestone-driven
//   * Ongoing Care (`care`)  — green — relationship, calendar-driven
//
// Each mode has three variants:
//   * base — primary chrome (top bar, pill background, focus rings)
//   * soft — surface backgrounds, banner fills, chip backgrounds
//   * deep — text on `soft` surfaces, deep-emphasis labels
//
// Hex values come from the Claude Design handoff (design_handoff_service_records).
// CSS variables `--mode-opp` / `--mode-opp-soft` / `--mode-opp-deep`
// + matching `proc` and `care` mirror these in styles.css.

export const modes = {
  opp: {
    base: "#4e98c6", // Opportunity blue — aliases colors.blue
    soft: "#deeef7", // Opportunity surfaces — aliases colors.blueTint
    deep: "#2a6da8", // Opportunity text on soft
  },
  proc: {
    base: "#c69220", // Processing amber
    soft: "#fdf0d1", // Processing surfaces
    deep: "#8a5a16", // Processing text on soft
  },
  care: {
    base: "#2a8e6e", // Ongoing Care green
    soft: "#dcefe8", // Ongoing Care surfaces
    deep: "#1f6a52", // Ongoing Care text on soft
  },
} as const;

export type ModeKey = keyof typeof modes;

// ─── Care renewal-status tokens ────────────────────────────────────
//
// Ongoing Care is status-driven, not progress-driven. A care plan is in
// exactly one of four renewal states — Overdue, Due soon, Needs review,
// Running — and THAT is the only meaningful color on the care surfaces.
// (The care-mode teal above is the chrome; status is the signal. Months
// are a recurring cycle, so there is deliberately no light→navy stage
// ramp here — a gradient across months would imply a direction that does
// not exist.)
//
// Mirrored as CSS vars `--st-overdue` / `--st-due` / `--st-review` /
// `--st-running` in styles.css (with brightened dark-mode variants).
//
// `reviewQuiet` is a desaturated blue used for Needs review in MASS fills
// (calendar health strips, workload bars) so the action statuses (overdue,
// due) read first — with thousands of plans sitting in needs-review, the
// full blue would otherwise swamp every strip and bury the overdue red.
// The status PILL/dot always uses the full `review` blue so the status is
// never ambiguous. See NEEDS_REVIEW_EMPHASIS in care-status.ts.
export const careStatus = {
  overdue: "#c0392b", // red — renewal date has passed
  due: "#b45309", // amber — within the due-soon window
  review: "#2c6ea3", // info blue — not started yet
  reviewQuiet: "#9db8cf", // desaturated blue — needs-review in mass fills
  running: "#1f8a4c", // green — live and comfortably out
} as const;

export type CareStatusToken = keyof typeof careStatus;

// ─── Gradients ─────────────────────────────────────────────────────
//
// These are the three gradient recipes used across the app — never
// invent a fourth without flagging in CLAUDE.md. The button gradient
// (light → mid → dark blue) is the brand's signature affordance for
// "do this".

export const gradients = {
  navy: "linear-gradient(160deg, #011e38 0%, #022c51 50%, #033d70 100%)",
  button: "linear-gradient(135deg, #5ba3d0 0%, #4e98c6 50%, #3a7ea8 100%)",
  yellow: "linear-gradient(135deg, #e8c24a 0%, #fed766 100%)",
} as const;

// ─── Font stacks ───────────────────────────────────────────────────

export const fonts = {
  // Gotham Bold (the real brand heading face — self-hosted, 2026-08-23,
  // replacing the Montserrat stand-in) for headings + nav labels. Montserrat
  // stays in the stack as the loading/failure fallback. Uppercased +
  // letter-spaced everywhere.
  heading:
    '"Gotham", "Montserrat", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  // Open Sans 400/600/700/800 for body copy.
  body:
    '"Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  mono: '"SF Mono", ui-monospace, Menlo, Monaco, Consolas, monospace',
} as const;

// ─── Gotham letter-spacing (tracking) scale ────────────────────────
//
// Grant's call, 2026-08-23: real Gotham Bold rendered too cramped —
// 157 places in the OS set the heading font with NO tracking at all,
// and the ~370 that did set it used ~20 unrelated values. The logo
// spaces STEWARDSHIP at roughly 0.10em; the OS needed "some spacing
// that makes sense," applied consistently.
//
// THE RULE: tracking scales INVERSELY with font-size, because caps
// need proportionally more air as they get smaller. A 10px all-caps
// label and a 34px headline genuinely want different values — this is
// how caps read at size, not a preference. Grant picked this
// ("size-aware") over a single flat number for exactly that reason.
//
// UPPERCASE gets the full value. Mixed/sentence case gets roughly
// half, because lowercase letterforms already carry their own spacing
// and tracking them out reads as stretched — a person's name or a
// sentence-case card title must NOT get 0.12em.
//
// Applying it (see the guard test in tests/web/design-tokens.test.ts):
// the sweep RAISES tracking up to this scale but never pulls it down,
// so two deliberate choices survive untouched — the brand wordmark
// (.top-nav-brand, intentionally wider at 0.14em) and tight numerals
// (negative tracking on KPI/pill values). New code should just use the
// scale.

export const tracking = {
  /** Uppercase (the default for headings, nav, buttons, labels). */
  caps: [
    { maxPx: 10.5, em: 0.12 },
    { maxPx: 12.5, em: 0.1 },
    { maxPx: 14, em: 0.08 },
    { maxPx: 17, em: 0.06 },
    { maxPx: 23, em: 0.04 },
    { maxPx: 29, em: 0.03 },
    { maxPx: Number.POSITIVE_INFINITY, em: 0.01 },
  ],
  /** Mixed / sentence case — names, quotes, sentence-case titles. */
  mixed: [
    { maxPx: 10.5, em: 0.06 },
    { maxPx: 12.5, em: 0.05 },
    { maxPx: 14, em: 0.04 },
    { maxPx: 17, em: 0.03 },
    { maxPx: 29, em: 0.02 },
    { maxPx: Number.POSITIVE_INFINITY, em: 0.01 },
  ],
} as const;

/**
 * The tracking (letter-spacing, in em) a Gotham run of `sizePx` should
 * use. `uppercase` defaults to true because the OS's heading font is
 * uppercase by doctrine nearly everywhere.
 */
export function trackingForSize(sizePx: number, uppercase = true): number {
  const scale = uppercase ? tracking.caps : tracking.mixed;
  for (const step of scale) {
    if (sizePx <= step.maxPx) return step.em;
  }
  // Unreachable — the last step is Infinity — but keeps the return total.
  return scale[scale.length - 1]!.em;
}

// ─── Typography scale ──────────────────────────────────────────────
//
// Internal-tool densities (tighter than the marketing site's scale).
// Body is 14px, page H1 is 26px, KPI big numbers are 44px.

export const type = {
  pageH1: { size: 26, letterSpacing: "0.06em", weight: 800 as const },
  zoneTitle: { size: 15, letterSpacing: "0.08em", weight: 800 as const },
  bigNumber: { size: 44, letterSpacing: "-0.01em", weight: 800 as const },
  body: { size: 14, lineHeight: 1.4 },
  bodyCompact: { size: 13, lineHeight: 1.4 },
  caption: { size: 12.5, lineHeight: 1.4 },
  eyebrow: {
    size: 10.5,
    letterSpacing: "0.14em",
    weight: 800 as const,
    textTransform: "uppercase" as const,
  },
  navLabel: {
    size: 11.5,
    letterSpacing: "0.1em",
    weight: 800 as const,
    textTransform: "uppercase" as const,
  },
  sidebarLabel: {
    size: 10.5,
    letterSpacing: "0.12em",
    weight: 800 as const,
    textTransform: "uppercase" as const,
  },
  button: {
    size: 11,
    letterSpacing: "0.06em",
    weight: 800 as const,
    textTransform: "uppercase" as const,
  },
} as const;

// ─── Spacing scale ─────────────────────────────────────────────────
//
// Handoff scale: 4/8/12/16/20/24/32/40/56/80px. Components pull from
// this rather than literal pixel values.

export const space = {
  0: 0,
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  7: 32,
  8: 40,
  9: 56,
  10: 80,
} as const;

// ─── Radii ─────────────────────────────────────────────────────────

export const radii = {
  xs: 4, // yellow swoosh, small pills
  sm: 6, // buttons, chips, inputs
  md: 10, // cards, tiles (the design's default)
  lg: 12,
  pill: 9999, // 999 in CSS but 9999 to avoid Infinity serialization quirks
  // Back-compat aliases for code that imported the v1 names.
  card: 10,
  control: 6,
} as const;

// ─── Shadows ───────────────────────────────────────────────────────
//
// Always navy-tinted. Don't use neutral gray shadows anywhere — that
// breaks the brand's elevation language.

export const shadows = {
  sm: "0 2px 8px rgba(2, 44, 81, 0.08)",
  md: "0 4px 16px rgba(2, 44, 81, 0.12)",
  lg: "0 8px 32px rgba(2, 44, 81, 0.16)",
  hover: "0 8px 28px rgba(2, 44, 81, 0.16)",
  button: "0 6px 18px rgba(2, 44, 81, 0.30)",
  blueGlow:
    "0 8px 24px rgba(78, 152, 198, 0.18), 0 2px 8px rgba(2, 44, 81, 0.08)",
} as const;

// ─── Motion ────────────────────────────────────────────────────────
//
// Honors `prefers-reduced-motion` at the CSS layer.

export const motion = {
  fast: "150ms cubic-bezier(0.16, 1, 0.3, 1)",
  base: "200ms cubic-bezier(0.16, 1, 0.3, 1)",
  slow: "250ms cubic-bezier(0.16, 1, 0.3, 1)",
  easeOut: "cubic-bezier(0.16, 1, 0.3, 1)",
  durFast: "150ms",
  durMed: "250ms",
  durSlow: "500ms",
} as const;

// ─── Z-index layers ────────────────────────────────────────────────

export const z = {
  nav: 40,
  sidebar: 30,
  dropdown: 200,
  modal: 1000,
  toast: 1100,
  tweaks: 100,
} as const;

// ─── Breakpoints ───────────────────────────────────────────────────
//
// Container queries on `.vp-frame { container: vp / inline-size }`
// drive the responsive collapse to 64px sidebar at <1100px and the
// mobile takeover at <500px.

export const breakpoints = {
  sm: 500,
  md: 768,
  lg: 1024,
  collapseSidebar: 1100,
  xl: 1280,
  contentMax: 1440,
} as const;

// ─── Per-staff avatar gradients ────────────────────────────────────
//
// Every advisor is recognizable by color across the whole app. Adding
// a new staff member: pick a new gradient + register it in CSS as
// `.rcard__avatar--<initial>`. Pairs with the Avatar primitive in
// ui.tsx.

export const avatarGradients = {
  default: "linear-gradient(135deg, #5ba3d0, #3a7ea8)", // blue
  grant: "linear-gradient(135deg, #b88dd9, #7e5ca5)", // purple
  joy: "linear-gradient(135deg, #5ba3d0, #3a7ea8)", // blue
  brian: "linear-gradient(135deg, #6bc084, #3a8d52)", // green
  donna: "linear-gradient(135deg, #f48fb1, #c2185b)", // pink
  mike: "linear-gradient(135deg, #e8b74a, #c69220)", // gold
  nate: "linear-gradient(135deg, #ff8a65, #d84315)", // orange
} as const;

// ─── Convenience grouped export ────────────────────────────────────

export const tokens = {
  colors,
  modes,
  careStatus,
  gradients,
  fonts,
  tracking,
  type,
  space,
  radii,
  shadows,
  motion,
  z,
  breakpoints,
  avatarGradients,
} as const;

export type DesignTokens = typeof tokens;
